import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRectangleXmark,
  faMagnifyingGlass,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../images/logo.png";

import {
  Accordion,
  Button,
  Container,
  Form,
  InputGroup,
  Toast,
  ToastContainer,
} from "react-bootstrap";

import StatusBadge from "./StatusBadge";
import EditReservationModal from "./EditReservationModal";

export default function ConsulterResaAdmin() {
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState("desc"); // desc = + récent
  const [hideClosed, setHideClosed] = useState(true); // coché par défaut

  const [reservationList, setReservationList] = useState(null);
  const [search, setSearch] = useState(""); // mot de passe admin
  const [reSearch, setReSearch] = useState(""); // filtre texte
  const [adminEmail, setAdminEmail] = useState("");
  const [adminToken, setAdminToken] = useState(
    sessionStorage.getItem("adminToken"),
  );

  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    variant: "success",
    message: "",
  });

  const token = "1654551514hhgfhfb45665454";

  const goHome = () => {
    sessionStorage.removeItem("adminToken");
    setReservationList(null);
    navigate("/");
  };

  const showToast = (variant, message) => {
    setToast({ show: true, variant, message });
  };

  async function getReservationList(e) {
    e.preventDefault();

    try {
      // 🔐 1) LOGIN ADMIN
      const loginRes = await fetch(
        `${process.env.REACT_APP_BAC_URL}/mail/admin/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: adminEmail,
            password: search,
          }),
        },
      );

      const loginData = await loginRes.json();

      if (loginRes.status !== 200) {
        showToast("danger", "Accès admin refusé");
        return;
      }

      // 🔐 2) STOCKER JWT
      sessionStorage.setItem("adminToken", loginData.token);
      setAdminToken(loginData.token);

      // 📦 3) CHARGER LES RÉSERVATIONS
      await fetchAllReservations(loginData.token);
    } catch (err) {
      showToast("danger", "Erreur réseau");
    }
  }
  async function fetchAllReservations(token) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BAC_URL}/mail/all-reservation`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (response.status === 200) {
        setReservationList(result.data);
        showToast("success", "Réservations chargées");
      } else {
        showToast("danger", "Session expirée");
        sessionStorage.removeItem("adminToken");
        setReservationList(null);
      }
    } catch {
      showToast("danger", "Erreur serveur");
    }
  }

  // Filtre texte (nouveau modèle)
  const filtered = useMemo(() => {
    if (!reservationList) return [];

    const q = reSearch.trim().toLowerCase();

    let data = [...reservationList];

    // 1️⃣ Masquer terminées / annulées / refusées
    if (hideClosed) {
      data = data.filter(
        (item) =>
          !["terminée", "annulée", "refusée"].includes(
            String(item.status || "").toLowerCase(),
          ),
      );
    }

    // 2️⃣ Recherche globale (TOUS les champs)
    if (q) {
      data = data.filter((item) => {
        return Object.values(item)
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      });
    }

    // 3️⃣ Tri par date + heure
    data.sort((a, b) => {
      const da = new Date(`${a.date}T${a.heure || "00:00"}`);
      const db = new Date(`${b.date}T${b.heure || "00:00"}`);

      return sortOrder === "asc" ? da - db : db - da;
    });

    return data;
  }, [reservationList, reSearch, sortOrder, hideClosed]);

  async function updateReservation(payload) {
    try {
      setLoadingUpdate(true);

      const url = `${process.env.REACT_APP_BAC_URL}/mail/update-statut`;

      const body = {
        id: payload.id,
        email: payload.email,

        // chez toi = clé admin
        reservationId: payload.reservationId,

        // nouveau
        status: payload.status,

        // compat legacy
        state: payload.status,

        commentaire: payload.commentaire,

        // prix/promo
        prix: payload.prix, // si forçage
        promoType: payload.promoType,
        promoValue: payload.promoValue,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: "bearer " + adminToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      console.log(response);

      if (response.status === 200) {
        showToast("success", "Réservation modifiée avec succès.");
        await fetchAllReservations(adminToken);
        setShowModal(false);
      } else {
        showToast("danger", "Échec de la modification. Réessaie.");
      }
    } catch (err) {
      showToast("danger", "Erreur réseau pendant la modification.");
    } finally {
      setLoadingUpdate(false);
    }
  }

  // --- LOGIN ---
  if (!reservationList) {
    return (
      <div className="admin-page">
        <Container className="admin-shell">
          <div className="admin-topbar">
            <img className="admin-logo" alt="Logo" src={logo} />
            <button className="close-btn" onClick={goHome} type="button">
              <FontAwesomeIcon icon={faRectangleXmark} />
            </button>
          </div>

          <div className="admin-card">
            <h3 className="admin-title">Accès Admin</h3>
            <p className="admin-subtitle">
              Saisis le mot de passe pour afficher toutes les réservations.
            </p>

            <Form onSubmit={getReservationList}>
              <InputGroup className="admin-search mb-2">
                <Form.Control
                  type="email"
                  placeholder="Email admin"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                />
              </InputGroup>
              <InputGroup className="admin-search">
                <Form.Control
                  type="password"
                  placeholder="Mot de passe"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <Button className="btn-primary-gold" type="submit">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="me-2" />
                  Ouvrir
                </Button>
              </InputGroup>
            </Form>
          </div>
        </Container>

        <ToastContainer position="bottom-end" className="p-3">
          <Toast
            show={toast.show}
            onClose={() => setToast((t) => ({ ...t, show: false }))}
            delay={2500}
            autohide
            bg={toast.variant}
          >
            <Toast.Body className="text-white">{toast.message}</Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
    );
  }

  // --- LISTE ---
  return (
    <div className="admin-page">
      <Container className="admin-shell">
        <div className="admin-topbar">
          <img className="admin-logo" alt="Logo" src={logo} />
          <div className="admin-topbar-right">
            <div className="admin-count">
              {filtered.length} / {reservationList.length}
            </div>
            <button className="close-btn" onClick={goHome} type="button">
              <FontAwesomeIcon icon={faRectangleXmark} />
            </button>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-head">
            <div>
              <h3 className="admin-title">Réservations</h3>
              <p className="admin-subtitle">
                Filtre par client, trajet, statut, date, téléphone…
              </p>
            </div>

            <InputGroup className="admin-filter">
              <InputGroup.Text>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Rechercher…"
                value={reSearch}
                onChange={(e) => setReSearch(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="admin-controls d-flex flex-wrap gap-3 mt-3 align-items-center">
            {/* TRI */}
            <Form.Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={{ maxWidth: 260 }}
            >
              <option value="desc">Plus récentes → plus anciennes</option>
              <option value="asc">Plus anciennes → plus récentes</option>
            </Form.Select>

            {/* MASQUER STATUTS */}
            <Form.Check
              type="switch"
              id="hide-closed"
              label="Masquer courses terminées / annulées / refusées"
              checked={hideClosed}
              onChange={(e) => setHideClosed(e.target.checked)}
            />
          </div>

          <Accordion alwaysOpen className="admin-accordion">
            {filtered.map((item, idx) => {
              const key = item._id || `${item.email}-${item.date}-${idx}`;

              return (
                <Accordion.Item
                  eventKey={key}
                  key={key}
                  className="admin-accordion-item"
                >
                  <Accordion.Header>
                    <div className="admin-row">
                      <div className="admin-row-left">
                        <div className="admin-name">
                          {item.firstname} {item.name}
                        </div>

                        <div className="admin-meta">
                          {item.date} • {item.heure} • {item.depart} →{" "}
                          {item.arrivee}
                        </div>

                        <div className="admin-meta-2">
                          {item.serviceType} • {item.passagers} pax •{" "}
                          {item.bagages} bag • {item.prix} €
                        </div>
                      </div>

                      <div className="admin-row-right">
                        <StatusBadge status={item.status} />
                        <button
                          type="button"
                          className="admin-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(item);
                            setShowModal(true);
                          }}
                          aria-label="Modifier"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                      </div>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    <div className="admin-details-grid">
                      <div className="admin-details-card">
                        <div className="admin-details-title">Coordonnées</div>
                        <div className="admin-details-line">
                          <strong>Email:</strong> {item.email}
                        </div>
                        <div className="admin-details-line">
                          <strong>Téléphone:</strong> {item.phone}
                        </div>
                      </div>

                      <div className="admin-details-card">
                        <div className="admin-details-title">Course</div>
                        <div className="admin-details-line">
                          <strong>Service:</strong> {item.serviceType}
                        </div>
                        <div className="admin-details-line">
                          <strong>Distance:</strong> {item.distance} km
                        </div>
                        <div className="admin-details-line">
                          <strong>Durée:</strong> {item.duree} min
                        </div>
                        <div className="admin-details-line">
                          <strong>Prix:</strong> {item.prix} €
                        </div>
                      </div>

                      <div className="admin-details-card">
                        <div className="admin-details-title">Demandes</div>
                        <div className="admin-details-line">
                          {item.demandes ? item.demandes : "—"}
                        </div>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      </Container>

      <EditReservationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        reservation={selected}
        onSubmit={updateReservation}
        loading={loadingUpdate}
      />

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          show={toast.show}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
          delay={2500}
          autohide
          bg={toast.variant}
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
